import { useId, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import InteractiveElementIllustration from "@/components/interactive/InteractiveElementIllustration";
import type { InteractiveIllustrationKind } from "@/lib/interactiveIllustration";

export const INTERACTIVE_ELEMENT_CARD_CLASS =
  "rounded-2xl border border-primary/25 bg-primary/5 px-6 py-6";

/** Shared left edge for hint and input text.
 *  The prompt inside `.quiz-entry-counter` is inline, so padding on
 *  `.quiz-question` would indent only the first line of a wrapped question.
 *  The counter is the block that holds every line. Review cards bring their
 *  own padding, so both insets reset there. */
const INLINE_TEXT_INSET_CLASS = [
  "[&_.quiz-entry-counter]:pl-3",
  "[&_.quiz-question]:pl-3",
  "[&_.quiz-entry-counter_.quiz-question]:pl-0",
  "[&_[data-slot=quiz-review-question]_.quiz-entry-counter]:pl-0",
  "[&_[data-slot=quiz-review-question]_.quiz-question]:pl-0",
].join(" ");

interface InteractiveElementInlineCardProps {
  title?: string;
  titleId?: string;
  illustrationKind: InteractiveIllustrationKind;
  children: ReactNode;
  className?: string;
}

/**
 * Tinted interactive-element card used by inlined quizzes and static
 * illustrated blocks (e.g. checklists) — illustration + title, then body.
 */
const InteractiveElementInlineCard = ({
  title,
  titleId: titleIdProp,
  illustrationKind,
  children,
  className,
}: InteractiveElementInlineCardProps) => {
  const generatedId = useId();
  const titleId = titleIdProp ?? generatedId;
  const heading = title?.trim() || undefined;

  return (
    <section
      aria-labelledby={heading ? titleId : undefined}
      className={cn("interactive-element", className)}
    >
      <div
        data-slot="interactive-element-card"
        className={INTERACTIVE_ELEMENT_CARD_CLASS}
      >
        <div
          data-slot="interactive-element-inline-layout"
          className="flex flex-col gap-4"
        >
          <div
            data-slot="interactive-element-inline-header"
            className="flex items-center gap-6 pl-3"
          >
            {/* -ml-1.5 cancels the file margin so the squircle meets this pl-3 text inset. */}
            <InteractiveElementIllustration
              kind={illustrationKind}
              className="-ml-1.5"
            />
            {heading && (
              <div
                data-slot="interactive-element-inline-title"
                className="min-w-0"
              >
                <h2 id={titleId}>{heading}</h2>
              </div>
            )}
          </div>
          <div
            data-slot="interactive-element-inline-body"
            className={cn("min-w-0 w-full", INLINE_TEXT_INSET_CLASS)}
          >
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveElementInlineCard;
